import React, { useState, useEffect } from 'react';
import axiosInstance from '../utils/axios';
import FirmaCanvasModal from '../components/FirmaCanvasModal';
import {
  Table, Button, Input, Modal, Form, Select, message,
  Card, Space, Tag, Descriptions, Drawer, Badge, Tabs, Input as AntInput, Divider, Tooltip, DatePicker, Popconfirm,
} from 'antd';
import { isAdmin } from '../utils/auth';
import {
  FileTextOutlined, PlusOutlined, SearchOutlined, HomeOutlined,
  UserOutlined, CarOutlined, ClockCircleOutlined, CheckCircleOutlined,
  CloseCircleOutlined, EditOutlined, CheckOutlined, CloseOutlined,
  UnorderedListOutlined, LinkOutlined, SignatureOutlined, CalendarOutlined,
  PhoneOutlined, DeleteOutlined,
} from '@ant-design/icons';
import dayjs from 'dayjs';
import { useNavigate } from 'react-router-dom';
import { hssColors, commonStyles } from '../config/theme';
import Footer from '../components/Footer';

const CAT_COLOR = { exterior: 'blue', interior: 'orange', documentos: 'green', equipamiento: 'purple' };
const ESTADO_BTN = {
  ok: { label: 'OK', activeStyle: { background: '#27ae60', borderColor: '#27ae60', color: '#fff' } },
  nf: { label: 'NF', activeStyle: { background: '#e74c3c', borderColor: '#e74c3c', color: '#fff' } },
  nt: { label: 'NT', activeStyle: { background: '#bdc3c7', borderColor: '#bdc3c7', color: '#636e72' } },
};

const { Option } = Select;

const SENSORES_INSTALACION = [
  'Sensor de Carreta', 'Puerta Trasera', 'Puerta Lateral',
  'Combustible', 'Temperatura', 'Motor en marcha',
  'Brazo Grúa', 'Ibutton',
];

const Ordenes = () => {
  const navigate = useNavigate();
  const [ordenes, setOrdenes] = useState([]);
  const [modalVisible, setModalVisible] = useState(false);
  const [editModalVisible, setEditModalVisible] = useState(false);
  const [selectedOrden, setSelectedOrden] = useState(null);
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [vehiculos, setVehiculos] = useState([]);
  const [usuarios, setUsuarios] = useState([]);
  const [clientes, setClientes] = useState([]);
  const [selectedCliente, setSelectedCliente] = useState(null);
  const [searchText, setSearchText] = useState('');
  const [soloMias, setSoloMias] = useState(false);
  const [paginaActual, setPaginaActual] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const userDni = localStorage.getItem('userDni') || '';

  // Checklist
  const [checklistDrawer, setChecklistDrawer] = useState(false);
  const [checklistData, setChecklistData] = useState({ inicial: null, final: null });
  const [tipoActivo, setTipoActivo] = useState('inicial');
  const [respuestas, setRespuestas] = useState({});
  const [loadingChecklist, setLoadingChecklist] = useState(false);
  const [savingChecklist, setSavingChecklist] = useState(false);
  const [firmaPresencialOpen, setFirmaPresencialOpen] = useState(false);
  const [nombrePresencial, setNombrePresencial] = useState('');
  const [savingPresencial, setSavingPresencial] = useState(false);
  const [linkFirma, setLinkFirma] = useState('');

  const estados = ['ACTIVO', 'COMPLETADO', 'CANCELADO'];
  const tipos = ['Instalacion', 'Desinstalacion', 'Service', 'Soporte', 'Interno', 'Desarrollo'];
  const tiposConVehiculo = new Set(['Instalacion', 'Desinstalacion', 'Service']);
  const tiposConCliente = new Set(['Instalacion', 'Desinstalacion', 'Service', 'Soporte']);
  const prioridades = [
    { value: 'BAJA',    color: 'default' },
    { value: 'MEDIA',   color: 'blue'    },
    { value: 'ALTA',    color: 'orange'  },
    { value: 'URGENTE', color: 'red'     },
  ];
  const [tipoNuevo, setTipoNuevo] = useState(null);
  const [editForm] = Form.useForm();

  useEffect(() => {
    cargarOrdenes();
    cargarUsuarios();
    cargarClientes();
  }, []);

  const cargarOrdenes = async () => {
    try {
      setLoading(true);
      const response = await axiosInstance.get('/ordenes');
      if (Array.isArray(response.data)) setOrdenes(response.data);
    } catch {
      message.error('Error al cargar órdenes');
    } finally {
      setLoading(false);
    }
  };

  const cargarVehiculos = async (clienteDni) => {
    try {
      if (!clienteDni) { setVehiculos([]); return; }
      const response = await axiosInstance.get(`/vehiculos?cliente_dni=${clienteDni}`);
      if (Array.isArray(response.data)) setVehiculos(response.data);
    } catch {
      message.error('Error al cargar vehículos');
    }
  };

  const cargarUsuarios = async () => {
    try {
      const response = await axiosInstance.get('/usuarios');
      setUsuarios(response.data);
    } catch {
      message.error('Error al cargar usuarios');
    }
  };

  const cargarClientes = async () => {
    try {
      const response = await axiosInstance.get('/clientes');
      if (Array.isArray(response.data)) setClientes(response.data);
    } catch {
      message.error('Error al cargar clientes');
    }
  };

  const handleRowClick = (record) => { abrirEditar(record); };

  const abrirEditar = (orden) => {
    setSelectedOrden(orden);
    const clienteDni = orden.vehiculo?.cliente?.dni || orden.cliente_dni || null;
    if (tiposConVehiculo.has(orden.tipo) && clienteDni) {
      cargarVehiculos(clienteDni);
      setSelectedCliente(clienteDni);
    } else if (orden.tipo === 'Soporte' && clienteDni) {
      setSelectedCliente(clienteDni);
    } else {
      setSelectedCliente(null);
      setVehiculos([]);
    }
    editForm.setFieldsValue({
      tipo: orden.tipo,
      estado: orden.estado,
      prioridad: orden.prioridad || 'MEDIA',
      cliente_dni: clienteDni || undefined,
      vehiculo_chapa: orden.vehiculo_chapa || undefined,
      contacto: orden.contacto || '',
      fecha_programada: orden.fecha_programada ? dayjs(orden.fecha_programada) : null,
      responsable_dni: orden.responsable_dni,
      observaciones: orden.observaciones || '',
      sensores_instalados: orden.sensores_instalados || [],
    });
    setTipoNuevo(orden.tipo);
    setEditModalVisible(true);
  };

  const handleEditarOrden = async (values) => {
    try {
      setLoading(true);
      const payload = {
        tipo: values.tipo,
        estado: values.estado,
        prioridad: values.prioridad,
        vehiculo_chapa: tiposConVehiculo.has(values.tipo) ? (values.vehiculo_chapa || null) : null,
        cliente_dni: tiposConCliente.has(values.tipo) ? (values.cliente_dni || null) : null,
        contacto: values.contacto || null,
        fecha_programada: values.fecha_programada ? values.fecha_programada.toISOString() : null,
        responsable_dni: values.responsable_dni,
        observaciones: values.observaciones || '',
        sensores_instalados: values.tipo === 'Instalacion' ? (values.sensores_instalados || []) : [],
      };
      const response = await axiosInstance.put(`/ordenes/${selectedOrden.id}`, payload);
      message.success('Orden actualizada');
      setOrdenes(prev => prev.map(o => o.id === selectedOrden.id ? response.data.orden : o));
      setEditModalVisible(false);
      editForm.resetFields();
      setTipoNuevo(null);
      setSelectedCliente(null);
      setVehiculos([]);
    } catch (error) {
      message.error(error.response?.data?.error || 'Error al actualizar');
    } finally {
      setLoading(false);
    }
  };

  const onFinish = async (values) => {
    try {
      setLoading(true);
      const payload = {
        tipo: values.tipo,
        prioridad: values.prioridad || 'MEDIA',
        vehiculo_chapa: tiposConVehiculo.has(values.tipo) ? (values.vehiculo_chapa || null) : null,
        cliente_dni: tiposConCliente.has(values.tipo) ? (values.cliente_dni || null) : null,
        contacto: values.contacto || null,
        fecha_programada: values.fecha_programada ? values.fecha_programada.toISOString() : null,
        responsable_dni: values.responsable_dni,
        observaciones: values.observaciones || '',
        sensores_instalados: values.tipo === 'Instalacion' ? (values.sensores_instalados || []) : [],
      };
      const response = await axiosInstance.post('/ordenes', payload);
      if (response.data.orden) {
        message.success('Orden creada exitosamente');
        setModalVisible(false);
        form.resetFields();
        setSelectedCliente(null);
        setVehiculos([]);
        cargarOrdenes();
      }
    } catch (error) {
      message.error(error.response?.data?.error || 'Error al crear orden');
    } finally {
      setLoading(false);
    }
  };

  // --- CHECKLIST ---
  const abrirChecklist = async (orden) => {
    setSelectedOrden(orden);
    setChecklistData({ inicial: null, final: null });
    setRespuestas({});
    setTipoActivo('inicial');
    setLinkFirma('');
    setNombrePresencial('');
    setChecklistDrawer(true);
    setLoadingChecklist(true);
    try {
      const res = await axiosInstance.get(`/checklist/orden/${orden.id}`);
      setChecklistData(res.data);
      cargarRespuestasEnEstado(res.data, 'inicial');
      const cl = res.data['inicial'];
      if (cl?.token_firma) setLinkFirma(`https://erp.hss.com.py/checklist/firma/${cl.token_firma}`);
    } catch { message.error('Error al cargar checklist'); }
    finally { setLoadingChecklist(false); }
  };

  const cargarRespuestasEnEstado = (data, tipo) => {
    const cl = data[tipo];
    if (!cl) { setRespuestas({}); return; }
    const map = {};
    cl.respuestas.forEach(r => { map[r.id] = { estado: r.estado, observacion: r.observacion || '' }; });
    setRespuestas(map);
  };

  const cambiarTab = (tipo) => {
    setTipoActivo(tipo);
    cargarRespuestasEnEstado(checklistData, tipo);
    const cl = checklistData[tipo];
    if (cl?.token_firma) setLinkFirma(`https://erp.hss.com.py/checklist/firma/${cl.token_firma}`);
    else setLinkFirma('');
  };

  const crearChecklist = async (tipo) => {
    setLoadingChecklist(true);
    try {
      const res = await axiosInstance.post(`/checklist/orden/${selectedOrden.id}`, { tipo });
      const nuevo = { ...checklistData, [tipo]: res.data };
      setChecklistData(nuevo);
      cargarRespuestasEnEstado(nuevo, tipo);
      message.success(`Checklist ${tipo} iniciado`);
    } catch (e) { message.error(e.response?.data?.error || 'Error'); }
    finally { setLoadingChecklist(false); }
  };

  const onEstado = (respId, estado) => {
    setRespuestas(prev => ({ ...prev, [respId]: { ...prev[respId], estado } }));
  };
  const onObservacion = (respId, observacion) => {
    setRespuestas(prev => ({ ...prev, [respId]: { ...prev[respId], observacion } }));
  };

  const guardarChecklist = async () => {
    const cl = checklistData[tipoActivo];
    if (!cl) return;
    setSavingChecklist(true);
    try {
      const payload = Object.entries(respuestas).map(([id, v]) => ({ id: parseInt(id), ...v }));
      const res = await axiosInstance.put(`/checklist/${cl.id}`, { respuestas: payload });
      setChecklistData(prev => ({ ...prev, [tipoActivo]: res.data }));
      message.success('Checklist guardado');
    } catch (e) { message.error(e.response?.data?.error || 'Error al guardar'); }
    finally { setSavingChecklist(false); }
  };

  const solicitarFirma = async () => {
    const cl = checklistData[tipoActivo];
    if (!cl) return;
    try {
      const res = await axiosInstance.post(`/checklist/${cl.id}/solicitar-firma`);
      const link = `https://erp.hss.com.py/checklist/firma/${res.data.token}`;
      setLinkFirma(link);
      if (navigator.clipboard?.writeText) {
        await navigator.clipboard.writeText(link);
      } else {
        const ta = document.createElement('textarea');
        ta.value = link;
        ta.style.cssText = 'position:fixed;opacity:0';
        document.body.appendChild(ta);
        ta.focus(); ta.select();
        try { document.execCommand('copy'); } catch {}
        document.body.removeChild(ta);
      }
      message.success('Link copiado: ' + link);
    } catch { message.error('Error al generar link de firma'); }
  };

  const firmarPresencial = async (firmaData) => {
    const cl = checklistData[tipoActivo];
    if (!cl) return;
    if (!nombrePresencial.trim()) { alert('Por favor ingrese el nombre del firmante.'); return; }
    setSavingPresencial(true);
    try {
      const res = await axiosInstance.post(`/checklist/${cl.id}/firmar-presencial`, {
        firma_data: firmaData,
        firmado_por: nombrePresencial,
      });
      const updated = { ...cl, firma_data: firmaData, firmado_por: res.data.firmado_por, firmado_at: new Date().toISOString(), tiene_firma: true };
      setChecklistData(prev => ({ ...prev, [tipoActivo]: updated }));
      setFirmaPresencialOpen(false);
      setNombrePresencial('');
      message.success(`Checklist firmado por ${res.data.firmado_por}`);
    } catch (e) {
      message.error(e.response?.data?.error || 'Error al firmar');
    } finally {
      setSavingPresencial(false);
    }
  };

  const getProgreso = (tipo) => {
    const cl = checklistData[tipo];
    if (!cl) return { total: 0, ok: 0, nf: 0, nt: 0 };
    const rs = cl.respuestas;
    return {
      total: rs.length,
      ok: rs.filter(r => r.estado === 'ok').length,
      nf: rs.filter(r => r.estado === 'nf' || r.estado === 'nok').length,
      nt: rs.filter(r => r.estado === 'nt' || r.estado === 'na').length,
    };
  };

  // --- HELPERS ---
  const getEstadoConfig = (estado) => {
    switch (estado) {
      case 'ACTIVO':    return { color: hssColors.info,    icon: <ClockCircleOutlined />,  bgColor: '#e3f2fd' };
      case 'COMPLETADO':return { color: hssColors.success, icon: <CheckCircleOutlined />,  bgColor: '#e8f5e9' };
      case 'CANCELADO': return { color: hssColors.danger,  icon: <CloseCircleOutlined />,  bgColor: '#ffebee' };
      default:          return { color: hssColors.gray400, icon: null,                     bgColor: '#f5f5f5' };
    }
  };

  const filteredOrdenes = ordenes.filter(orden => {
    if (soloMias && String(orden.responsable_dni) !== String(userDni)) return false;
    if (!searchText) return true;
    const s = searchText.toLowerCase();
    const v = orden.vehiculo ? `${orden.vehiculo.marca} ${orden.vehiculo.modelo} ${orden.vehiculo.chapa}`.toLowerCase() : '';
    const r = orden.responsable ? orden.responsable.nombre.toLowerCase() : '';
    return orden.id?.toString().includes(s) || orden.numero_orden?.toString().includes(s) || orden.tipo?.toLowerCase().includes(s) ||
           orden.estado?.toLowerCase().includes(s) || orden.observaciones?.toLowerCase().includes(s) ||
           v.includes(s) || r.includes(s) ||
           orden.vehiculo?.nombre_gps?.toLowerCase().includes(s) ||
           orden.vehiculo?.imei?.includes(s);
  });

  const onEliminarOrden = async (id) => {
    try {
      await axiosInstance.delete(`/ordenes/${id}`);
      message.success('Orden eliminada');
      setOrdenes(prev => prev.filter(o => o.id !== id));
    } catch (e) { message.error(e.response?.data?.error || 'Error al eliminar'); }
  };

  const columns = [
    {
      title: 'N°', dataIndex: 'id', key: 'id', width: 80,
      sorter: (a, b) => a.id - b.id,
      render: (id, record) => <span style={{ fontWeight: '600', color: hssColors.primary }}>#{record.numero_orden || id}</span>,
    },
    {
      title: 'Tipo', dataIndex: 'tipo', key: 'tipo',
      sorter: (a, b) => (a.tipo || '').localeCompare(b.tipo || ''),
      render: tipo => <Space><FileTextOutlined style={{ color: hssColors.warning }} /><span style={{ fontWeight: '500' }}>{tipo}</span></Space>,
    },
    {
      title: 'Prioridad', dataIndex: 'prioridad', key: 'prioridad', width: 100,
      sorter: (a, b) => {
        const ord = { BAJA: 0, MEDIA: 1, ALTA: 2, URGENTE: 3 };
        return (ord[a.prioridad] ?? 1) - (ord[b.prioridad] ?? 1);
      },
      render: p => {
        const cfg = { BAJA: 'default', MEDIA: 'blue', ALTA: 'orange', URGENTE: 'red' };
        return <Tag color={cfg[p] || 'blue'} style={{ fontWeight: 600 }}>{p || 'MEDIA'}</Tag>;
      },
    },
    {
      title: 'Estado', dataIndex: 'estado', key: 'estado',
      sorter: (a, b) => (a.estado || '').localeCompare(b.estado || ''),
      render: estado => {
        const c = getEstadoConfig(estado);
        return <Tag icon={c.icon} color={c.color} style={{ fontWeight: '600', padding: '4px 12px', borderRadius: '6px' }}>{estado}</Tag>;
      },
    },
    {
      title: 'Vehículo / Cliente', key: 'vehiculo',
      sorter: (a, b) => (a.vehiculo?.chapa || a.cliente?.nombre || '').localeCompare(b.vehiculo?.chapa || b.cliente?.nombre || ''),
      render: (_, r) => {
        const cliente = r.vehiculo?.cliente || r.cliente;
        if (r.vehiculo) return (
          <div>
            <Space><CarOutlined style={{ color: hssColors.secondary }} /><span>{`${r.vehiculo.marca} ${r.vehiculo.modelo} (${r.vehiculo.chapa})`}</span></Space>
            {cliente && <div style={{ fontSize: 11, color: hssColors.textSecondary, marginTop: 2, paddingLeft: 2 }}><UserOutlined style={{ marginRight: 4 }} />{cliente.nombre} {cliente.apellido || ''}</div>}
            {r.tipo === 'Instalacion' && r.sensores_instalados?.length > 0 && (
              <div style={{ marginTop: 4, display: 'flex', flexWrap: 'wrap', gap: 3 }}>
                {r.sensores_instalados.map(s => <Tag key={s} color="blue" style={{ fontSize: 10, padding: '0 4px', margin: 0 }}>{s}</Tag>)}
              </div>
            )}
          </div>
        );
        if (r.cliente) return <Space><UserOutlined style={{ color: hssColors.info }} /><span style={{ color: hssColors.info }}>{r.cliente.nombre} {r.cliente.apellido || ''}</span></Space>;
        return <span style={{ color: hssColors.gray400 }}>No especificado</span>;
      },
    },
    {
      title: 'Responsable', key: 'responsable',
      sorter: (a, b) => (a.responsable?.nombre || '').localeCompare(b.responsable?.nombre || ''),
      render: (_, r) => r.responsable
        ? <Space><UserOutlined style={{ color: hssColors.success }} /><span>{r.responsable.nombre}</span></Space>
        : <span style={{ color: hssColors.gray400 }}>No asignado</span>,
    },
    {
      title: 'Fecha prog.', key: 'fecha',
      sorter: (a, b) => new Date(a.fecha_programada || a.fecha_creacion || 0) - new Date(b.fecha_programada || b.fecha_creacion || 0),
      render: (_, r) => {
        const fecha = r.fecha_programada || r.fecha_creacion;
        if (!fecha) return '-';
        return (
          <Space>
            <CalendarOutlined style={{ color: r.fecha_programada ? hssColors.warning : hssColors.info }} />
            <span style={{ color: r.fecha_programada ? undefined : hssColors.textSecondary }}>
              {new Date(fecha).toLocaleDateString()}
            </span>
          </Space>
        );
      },
    },
    {
      title: 'Acciones', key: 'acciones',
      render: (_, record) => {
        const esVision = record.vehiculo?.servicio === 'VISION';
        const tieneChecklist = record.vehiculo && tiposConVehiculo.has(record.tipo) && !esVision;
        return (
          <Space>
            <Button type="primary" ghost icon={<EditOutlined />} onClick={(e) => { e.stopPropagation(); handleRowClick(record); }} style={{ borderRadius: '6px' }}>
              Editar
            </Button>
            {tieneChecklist && (
              <Button icon={<UnorderedListOutlined />} onClick={(e) => { e.stopPropagation(); abrirChecklist(record); }} style={{ borderRadius: '6px' }}>
                Checklist
              </Button>
            )}
            {isAdmin() && (
              <Popconfirm
                title="¿Eliminar esta orden?"
                description="Esta acción no se puede deshacer."
                onConfirm={(e) => { onEliminarOrden(record.id); }}
                okText="Sí, eliminar" cancelText="No"
                okButtonProps={{ danger: true }}
              >
                <Button danger type="text" icon={<DeleteOutlined />} onClick={e => e.stopPropagation()} />
              </Popconfirm>
            )}
          </Space>
        );
      },
    },
  ];

  const handleClienteChange = (value) => {
    setSelectedCliente(value);
    form.setFieldsValue({ vehiculo_chapa: undefined });
    cargarVehiculos(value);
  };


  return (
    <div style={{ minHeight: '100vh', background: hssColors.backgroundPrimary, display: 'flex', flexDirection: 'column' }}>
      {/* Header */}
      <div style={{ background: hssColors.backgroundSecondary, borderBottom: `1px solid ${hssColors.gray200}`, padding: '16px 32px' }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
            <FileTextOutlined style={{ fontSize: 28, color: hssColors.primary }} />
            <div>
              <h1 style={{ margin: 0, fontSize: 24, fontWeight: 700, color: hssColors.primary }}>Gestión de Órdenes de Trabajo</h1>
              <p style={{ margin: 0, fontSize: 13, color: hssColors.textSecondary }}>Administración de órdenes del sistema</p>
            </div>
          </div>
          <Space>
            <Button icon={<HomeOutlined />} onClick={() => navigate('/dashboard')} size="large" style={{ height: 44, borderRadius: 8 }}>Inicio</Button>
            <Button type="primary" icon={<PlusOutlined />} onClick={() => setModalVisible(true)} size="large" style={{ height: 44, borderRadius: 8, fontWeight: 600 }}>
              Nueva Orden
            </Button>
          </Space>
        </div>
      </div>

      {/* Contenido */}
      <div style={{ padding: '24px 32px', flex: 1 }}>
        {/* Stats */}
        <div style={{ display: 'flex', gap: 16, marginBottom: 24 }}>
          {[
            { label: 'Total órdenes', value: ordenes.length, icon: <FileTextOutlined />, color: hssColors.primary },
            { label: 'Activas', value: ordenes.filter(o => o.estado === 'ACTIVO').length, icon: <ClockCircleOutlined />, color: hssColors.warning },
            { label: 'Completadas', value: ordenes.filter(o => o.estado === 'COMPLETADO').length, icon: <CheckCircleOutlined />, color: hssColors.success },
            { label: 'Canceladas', value: ordenes.filter(o => o.estado === 'CANCELADO').length, icon: <CloseCircleOutlined />, color: hssColors.error },
          ].map(s => (
            <Card key={s.label} loading={loading} style={{ flex: 1, borderRadius: 12, border: 'none', boxShadow: commonStyles.shadowMd }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                <div style={{ fontSize: 28, color: s.color }}>{s.icon}</div>
                <div>
                  <div style={{ fontSize: 28, fontWeight: 700, color: s.color, lineHeight: 1 }}>{s.value}</div>
                  <div style={{ fontSize: 12, color: hssColors.textSecondary }}>{s.label}</div>
                </div>
              </div>
            </Card>
          ))}
        </div>

        <Card style={{ marginBottom: '24px', borderRadius: '12px', border: 'none', boxShadow: commonStyles.shadowMd }} styles={{ body: { padding: '20px 24px' } }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 12, flexWrap: 'wrap' }}>
            <Input
              placeholder="Buscar por ID, tipo, estado, vehículo, responsable..."
              prefix={<SearchOutlined style={{ color: hssColors.gray400 }} />}
              style={{ flex: 1, minWidth: 300, borderRadius: '8px' }}
              size="large"
              value={searchText}
              onChange={e => { setSearchText(e.target.value); setPaginaActual(1); }}
              allowClear
            />
            <Button
              size="large"
              icon={<UserOutlined />}
              type={soloMias ? 'primary' : 'default'}
              onClick={() => { setSoloMias(v => !v); setPaginaActual(1); }}
              style={{ borderRadius: '8px', fontWeight: 600 }}
            >
              Mis órdenes {soloMias && <Tag color="white" style={{ color: hssColors.primary, marginLeft: 4, fontWeight: 700 }}>{filteredOrdenes.length}</Tag>}
            </Button>
          </div>
        </Card>

        <Card style={{ borderRadius: '12px', border: 'none', boxShadow: commonStyles.shadowMd }} styles={{ body: { padding: 0 } }}>
          <Table
            columns={columns}
            dataSource={filteredOrdenes}
            rowKey="id"
            loading={loading}
            scroll={{ x: 'max-content' }}
            pagination={{ current: paginaActual, pageSize, showSizeChanger: true, pageSizeOptions: ['10', '20', '50', '100'], onChange: (p, size) => { setPaginaActual(p); setPageSize(size); }, showTotal: total => `Total: ${total} órdenes` }}
            style={{ borderRadius: '12px', overflow: 'hidden' }}
          />
        </Card>
      </div>

      {/* Modal: Editar orden */}
      <Modal
        title={
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px', fontSize: '20px', fontWeight: '700', color: hssColors.primary }}>
            <FileTextOutlined /><span>Editar Orden #{selectedOrden?.numero_orden || selectedOrden?.id}</span>
          </div>
        }
        open={editModalVisible}
        onCancel={() => { setEditModalVisible(false); editForm.resetFields(); setTipoNuevo(null); setSelectedCliente(null); setVehiculos([]); }}
        footer={[
          <Button key="checklist" icon={<UnorderedListOutlined />}
            onClick={() => { setEditModalVisible(false); abrirChecklist(selectedOrden); }}
            size="large" style={{ borderRadius: '8px' }}>
            Checklist
          </Button>,
          <Button key="cancel" onClick={() => { setEditModalVisible(false); editForm.resetFields(); setTipoNuevo(null); }} size="large" style={{ borderRadius: '8px' }}>
            Cancelar
          </Button>,
          <Button key="submit" type="primary" loading={loading}
            onClick={() => editForm.submit()} size="large" style={{ borderRadius: '8px', fontWeight: '600' }}>
            Guardar cambios
          </Button>,
        ]}
        width={700}
      >
        <Form form={editForm} onFinish={handleEditarOrden} layout="vertical" style={{ marginTop: 20 }}>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0 16px' }}>
            <Form.Item name="tipo" label="Tipo" rules={[{ required: true }]}>
              <Select size="large" onChange={v => { setTipoNuevo(v); editForm.setFieldsValue({ vehiculo_chapa: undefined, cliente_dni: undefined }); setVehiculos([]); setSelectedCliente(null); }}>
                {tipos.map(t => <Option key={t} value={t}>{t}</Option>)}
              </Select>
            </Form.Item>
            <Form.Item name="prioridad" label="Prioridad" rules={[{ required: true }]}>
              <Select size="large">
                {prioridades.map(p => <Option key={p.value} value={p.value}><Tag color={p.color}>{p.value}</Tag></Option>)}
              </Select>
            </Form.Item>
            <Form.Item name="estado" label="Estado" rules={[{ required: true }]}>
              <Select size="large">
                {estados.map(e => { const c = getEstadoConfig(e); return <Option key={e} value={e}><Tag icon={c.icon} color={c.color}>{e}</Tag></Option>; })}
              </Select>
            </Form.Item>
            <Form.Item name="responsable_dni" label="Responsable" rules={[{ required: true }]}>
              <Select size="large" showSearch optionFilterProp="children">
                {usuarios.map(u => <Option key={u.dni} value={u.dni}>{u.nombre}</Option>)}
              </Select>
            </Form.Item>
          </div>
          {tiposConCliente.has(tipoNuevo) && (
            <Form.Item name="cliente_dni" label="Cliente">
              <Select size="large" showSearch optionFilterProp="label" placeholder="Seleccione cliente (opcional)"
                onChange={v => { editForm.setFieldsValue({ vehiculo_chapa: undefined }); setSelectedCliente(v); if (tiposConVehiculo.has(tipoNuevo)) cargarVehiculos(v); }} allowClear>
                {clientes.map(c => <Option key={c.dni} value={c.dni} label={`${c.nombre} ${c.apellido || ''}`}>{c.nombre} {c.apellido || ''}</Option>)}
              </Select>
            </Form.Item>
          )}
          {tiposConVehiculo.has(tipoNuevo) && (
            <Form.Item name="vehiculo_chapa" label="Vehículo">
              <Select size="large" disabled={!selectedCliente} placeholder={selectedCliente ? 'Seleccione vehículo' : 'Primero seleccione cliente'} allowClear>
                {vehiculos.map(v => <Option key={v.chapa} value={v.chapa}>{v.marca} {v.modelo} ({v.chapa})</Option>)}
              </Select>
            </Form.Item>
          )}
          {tiposConCliente.has(tipoNuevo) && (
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0 16px' }}>
              <Form.Item name="contacto" label="Contacto / Nombre de quien llama">
                <Input size="large" prefix={<PhoneOutlined style={{ color: hssColors.gray400 }} />} placeholder="Ej: Rodrigo cel 0981-xxx" style={{ borderRadius: 8 }} />
              </Form.Item>
              <Form.Item name="fecha_programada" label="Fecha programada">
                <DatePicker size="large" style={{ width: '100%', borderRadius: 8 }} format="DD/MM/YYYY" placeholder="Seleccione fecha" />
              </Form.Item>
            </div>
          )}
          {tipoNuevo === 'Instalacion' && (
            <Form.Item name="sensores_instalados" label="Sensores instalados">
              <Select
                mode="tags"
                size="large"
                style={{ width: '100%' }}
                placeholder="Seleccioná o escribí un sensor y presioná Enter para agregar"
                tokenSeparators={[',']}
              >
                {SENSORES_INSTALACION.map(s => <Option key={s} value={s}>{s}</Option>)}
              </Select>
            </Form.Item>
          )}
          <Form.Item name="observaciones" label="Observaciones / Descripción">
            <Input.TextArea rows={3} style={{ borderRadius: 8 }} placeholder="Descripción de la tarea, notas, contexto..." />
          </Form.Item>
        </Form>
      </Modal>

      {/* Modal: Nueva orden */}
      <Modal
        title={
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px', fontSize: '20px', fontWeight: '700', color: hssColors.primary }}>
            <FileTextOutlined /><span>Nueva Orden de Trabajo</span>
          </div>
        }
        open={modalVisible}
        onCancel={() => { setModalVisible(false); form.resetFields(); setSelectedCliente(null); setVehiculos([]); }}
        footer={null}
        width={700}
      >
        <Form form={form} onFinish={onFinish} layout="vertical" style={{ marginTop: '24px' }}>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0 16px' }}>
            <Form.Item name="tipo" label="Tipo" rules={[{ required: true }]}>
              <Select size="large" onChange={v => { setTipoNuevo(v); form.setFieldsValue({ vehiculo_chapa: undefined, cliente_dni: undefined }); setVehiculos([]); setSelectedCliente(null); }}>
                {tipos.map(t => <Option key={t} value={t}>{t}</Option>)}
              </Select>
            </Form.Item>
            <Form.Item name="prioridad" label="Prioridad" initialValue="MEDIA" rules={[{ required: true }]}>
              <Select size="large">
                {prioridades.map(p => <Option key={p.value} value={p.value}><Tag color={p.color}>{p.value}</Tag></Option>)}
              </Select>
            </Form.Item>
          </div>

          <Form.Item name="responsable_dni" label="Responsable" rules={[{ required: true }]}>
            <Select placeholder="Seleccione el técnico responsable" size="large" showSearch optionFilterProp="label">
              {usuarios.map(u => <Option key={u.dni} value={u.dni} label={u.nombre}><Space><UserOutlined />{u.nombre}</Space></Option>)}
            </Select>
          </Form.Item>

          {tiposConCliente.has(tipoNuevo) && (
            <Form.Item name="cliente_dni" label="Cliente">
              <Select onChange={v => { if (tiposConVehiculo.has(tipoNuevo)) handleClienteChange(v); else setSelectedCliente(v); }}
                placeholder="Seleccione un cliente (opcional)" size="large" showSearch optionFilterProp="label" allowClear>
                {clientes.map(c => <Option key={c.dni} value={c.dni} label={`${c.nombre} ${c.apellido || ''}`}><Space><UserOutlined />{c.nombre} {c.apellido || ''}</Space></Option>)}
              </Select>
            </Form.Item>
          )}
          {tiposConVehiculo.has(tipoNuevo) && (
            <Form.Item name="vehiculo_chapa" label="Vehículo">
              <Select disabled={!selectedCliente} placeholder={selectedCliente ? 'Seleccione un vehículo' : 'Primero seleccione un cliente'} size="large" allowClear>
                {vehiculos.map(v => <Option key={v.chapa} value={v.chapa}><Space><CarOutlined />{v.marca} {v.modelo} ({v.chapa})</Space></Option>)}
              </Select>
            </Form.Item>
          )}
          {tiposConCliente.has(tipoNuevo) && (
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0 16px' }}>
              <Form.Item name="contacto" label="Contacto / Quién llama">
                <Input size="large" prefix={<PhoneOutlined style={{ color: hssColors.gray400 }} />} placeholder="Ej: Rodrigo cel 0981-xxx" style={{ borderRadius: 8 }} />
              </Form.Item>
              <Form.Item name="fecha_programada" label="Fecha programada">
                <DatePicker size="large" style={{ width: '100%', borderRadius: 8 }} format="DD/MM/YYYY" placeholder="Seleccione fecha" />
              </Form.Item>
            </div>
          )}

          {tipoNuevo === 'Instalacion' && (
            <Form.Item name="sensores_instalados" label="Sensores instalados">
              <Select
                mode="tags"
                size="large"
                style={{ width: '100%' }}
                placeholder="Seleccioná o escribí un sensor y presioná Enter para agregar"
                tokenSeparators={[',']}
              >
                {SENSORES_INSTALACION.map(s => <Option key={s} value={s}>{s}</Option>)}
              </Select>
            </Form.Item>
          )}
          <Form.Item name="observaciones" label="Observaciones / Descripción">
            <Input.TextArea rows={3} placeholder="Descripción de la tarea, contexto, notas..." style={{ borderRadius: '8px' }} />
          </Form.Item>

          <Form.Item style={{ marginBottom: 0, marginTop: '16px' }}>
            <Space style={{ width: '100%', justifyContent: 'flex-end' }}>
              <Button onClick={() => { setModalVisible(false); form.resetFields(); setSelectedCliente(null); setVehiculos([]); setTipoNuevo(null); }} size="large" style={{ borderRadius: '8px' }}>
                Cancelar
              </Button>
              <Button type="primary" htmlType="submit" loading={loading} size="large" style={{ borderRadius: '8px', fontWeight: '600', boxShadow: commonStyles.shadowMd }}>
                Crear Orden
              </Button>
            </Space>
          </Form.Item>
        </Form>
      </Modal>

      <Footer />

      {/* Drawer: Checklist */}
      <Drawer
        title={
          <Space>
            <UnorderedListOutlined style={{ color: hssColors.primary }} />
            <span style={{ fontWeight: 700, color: hssColors.primary }}>Checklist — Orden #{selectedOrden?.numero_orden || selectedOrden?.id}</span>
          </Space>
        }
        open={checklistDrawer}
        onClose={() => setChecklistDrawer(false)}
        width={660}
        footer={
          checklistData[tipoActivo] ? (
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
              <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center' }}>
                {checklistData[tipoActivo]?.firmado_por ? (
                  <Tag color="success" icon={<CheckCircleOutlined />} style={{ fontSize: 13, padding: '4px 12px' }}>
                    Firmado por {checklistData[tipoActivo].firmado_por}
                  </Tag>
                ) : (
                  <>
                    <Button
                      icon={<SignatureOutlined />}
                      type="primary" ghost
                      onClick={() => setFirmaPresencialOpen(true)}
                      style={{ borderRadius: 8 }}
                    >
                      Firmar en persona
                    </Button>
                    <Tooltip title="Genera un link para que el cliente firme desde su celular">
                      <Button icon={<LinkOutlined />} onClick={solicitarFirma} style={{ borderRadius: 8 }}>
                        Enviar link
                      </Button>
                    </Tooltip>
                    {linkFirma && (
                      <Button
                        style={{ borderRadius: 8, background: '#25d366', borderColor: '#25d366', color: '#fff' }}
                        onClick={() => window.open(`https://wa.me/?text=${encodeURIComponent('Por favor firma el checklist de instalación HSS: ' + linkFirma)}`, '_blank')}
                      >
                        📱 WhatsApp
                      </Button>
                    )}
                  </>
                )}
              </div>
              <Button type="primary" loading={savingChecklist} onClick={guardarChecklist}
                icon={<CheckOutlined />} style={{ borderRadius: 8, fontWeight: 600 }}>
                Guardar
              </Button>
            </div>
          ) : null
        }
      >
        {loadingChecklist ? (
          <div style={{ textAlign: 'center', padding: '60px 0', color: hssColors.textSecondary }}>Cargando...</div>
        ) : (
          <Tabs
            activeKey={tipoActivo}
            onChange={cambiarTab}
            items={['inicial', 'final'].map(tipo => {
              const cl = checklistData[tipo];
              const prog = getProgreso(tipo);
              return {
                key: tipo,
                label: (
                  <Space>
                    <span style={{ textTransform: 'capitalize', fontWeight: 600 }}>{tipo}</span>
                    {cl ? (
                      cl.firmado_por
                        ? <Tag color="success" style={{ fontSize: 11 }}>Firmado</Tag>
                        : <Tag color={prog.nf > 0 ? 'error' : 'processing'} style={{ fontSize: 11 }}>
                            {prog.ok}/{prog.total} OK
                          </Tag>
                    ) : <Tag color="default" style={{ fontSize: 11 }}>Pendiente</Tag>}
                  </Space>
                ),
                children: !cl ? (
                  <div style={{ textAlign: 'center', padding: '48px 20px' }}>
                    <UnorderedListOutlined style={{ fontSize: 56, color: hssColors.gray400, marginBottom: 20 }} />
                    <h3 style={{ color: hssColors.textPrimary, marginBottom: 8 }}>
                      Checklist {tipo} no iniciado
                    </h3>
                    <p style={{ color: hssColors.textSecondary, marginBottom: 28 }}>
                      {tipo === 'inicial'
                        ? 'Registra el estado del vehículo antes de comenzar el trabajo.'
                        : 'Registra el estado del vehículo al finalizar el trabajo.'}
                    </p>
                    <Button type="primary" size="large" icon={<PlusOutlined />}
                      onClick={() => crearChecklist(tipo)} style={{ borderRadius: 8, fontWeight: 600 }}>
                      Iniciar checklist {tipo}
                    </Button>
                  </div>
                ) : (
                  <div>
                    {/* Resumen */}
                    <Card size="small" style={{ marginBottom: 16, borderRadius: 10, background: hssColors.gray50, border: 'none' }}>
                      <Space>
                        <Badge color="#27ae60" text={<span style={{ fontSize: 12 }}>{prog.ok} OK</span>} />
                        <Badge color="#e74c3c" text={<span style={{ fontSize: 12 }}>{prog.nf} NF</span>} />
                        <Badge color="#bdc3c7" text={<span style={{ fontSize: 12 }}>{prog.nt} NT</span>} />
                        {cl.firmado_por && (
                          <Tag color="success" icon={<SignatureOutlined />}>
                            Firmado por {cl.firmado_por}
                          </Tag>
                        )}
                      </Space>
                    </Card>

                    {/* Ítems agrupados por categoría */}
                    {(() => {
                      const porCat = {};
                      cl.respuestas.forEach(r => {
                        if (!porCat[r.categoria]) porCat[r.categoria] = [];
                        porCat[r.categoria].push(r);
                      });
                      return Object.entries(porCat).map(([cat, items]) => (
                        <div key={cat} style={{ marginBottom: 20 }}>
                          <div style={{ marginBottom: 8 }}>
                            <Tag color={CAT_COLOR[cat] || 'default'} style={{ fontSize: 12, padding: '2px 10px' }}>
                              {cat.charAt(0).toUpperCase() + cat.slice(1)}
                            </Tag>
                          </div>
                          {items.map(r => {
                            const rv = respuestas[r.id] || { estado: r.estado, observacion: r.observacion || '' };
                            const estadoNorm = rv.estado === 'nok' ? 'nf' : rv.estado === 'na' ? 'nt' : rv.estado;
                            const isNf = estadoNorm === 'nf';
                            return (
                              <div key={r.id} style={{
                                padding: '10px 12px', marginBottom: 6, borderRadius: 8,
                                border: `1px solid ${isNf ? '#ffa39e' : '#f0f0f0'}`,
                                background: isNf ? '#fff1f0' : '#fafafa',
                              }}>
                                <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: isNf ? 8 : 0 }}>
                                  <span style={{ flex: 1, fontSize: 13, color: hssColors.textPrimary }}>{r.descripcion}</span>
                                  <Button.Group size="small">
                                    {['ok', 'nf', 'nt'].map(e => (
                                      <Button key={e}
                                        style={estadoNorm === e ? ESTADO_BTN[e].activeStyle : {}}
                                        onClick={() => onEstado(r.id, e)}
                                        disabled={!!cl.firmado_por}
                                      >
                                        {ESTADO_BTN[e].label}
                                      </Button>
                                    ))}
                                  </Button.Group>
                                </div>
                                {isNf && (
                                  <AntInput.TextArea
                                    rows={2} size="small"
                                    placeholder="Describir el problema..."
                                    value={rv.observacion}
                                    onChange={e => onObservacion(r.id, e.target.value)}
                                    style={{ borderRadius: 6, fontSize: 12 }}
                                    disabled={!!cl.firmado_por}
                                  />
                                )}
                              </div>
                            );
                          })}
                        </div>
                      ));
                    })()}

                    {/* Firma existente */}
                    {cl.firma_data && (
                      <div style={{ marginTop: 16, textAlign: 'center' }}>
                        <Divider>Firma del cliente</Divider>
                        <img src={cl.firma_data} alt="Firma" style={{ maxWidth: 280, border: '1px solid #dee2e6', borderRadius: 8 }} />
                      </div>
                    )}
                  </div>
                ),
              };
            })}
          />
        )}
      </Drawer>

      <FirmaCanvasModal
        open={firmaPresencialOpen}
        onClose={() => { setFirmaPresencialOpen(false); setNombrePresencial(''); }}
        onConfirm={firmarPresencial}
        loading={savingPresencial}
        titulo="Firma presencial"
        nombreLabel="Nombre del firmante"
        nombreValue={nombrePresencial}
        onNombreChange={setNombrePresencial}
      />
    </div>
  );
};

export default Ordenes;
